import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { BalanceCommonSettings, PaymentMethodSetting, PaymentMethodSettingsKeys } from '@p2p/platform-settings/domain';
import _ from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';

import { SETTINGS_FORM_GROUP_NAME } from '../../constants';
import { UpsertUserData } from '../upsert-user';
import { UserRole } from '../user-role';

import { UserBaseForm, UserBaseFormControls } from './user-base.form';

export enum UserTraderFormControls {
    TeamLeadId = 'teamLeadId',
    MinTrustBalance = 'minTrustBalance',
    MinTradeBalance = 'minTradeBalance',
    PaymentMethodId = 'paymentMethodId',
    DailyLimit = 'dailyLimit',
    ThresholdOrderAmount = 'thresholdOrderAmount',
    UpToThresholdCommissionRate = 'upToThresholdCommissionRate',
    EqualOrAboveThresholdCommissionRate = 'equalOrAboveThresholdCommissionRate'
}

export interface FormPaymentMethodSetting {
    [UserTraderFormControls.PaymentMethodId]: number;
    [UserTraderFormControls.DailyLimit]: string;
    [UserTraderFormControls.ThresholdOrderAmount]: string;
    [UserTraderFormControls.UpToThresholdCommissionRate]: string;
    [UserTraderFormControls.EqualOrAboveThresholdCommissionRate]: string;
}

export const PAYMENT_METHOD_SETTINGS_FORM_ARRAY_NAME = 'paymentMethodSettings';

export class UserTraderForm extends UserBaseForm {
    override role = UserRole.Trader;

    readonly settingsFormGroup = new FormGroup<any>({});
    readonly paymentMethodSettingsFormArray = new FormArray<any>([]);

    get currentPaymentMethodsIds$(): Observable<number[]> {
        return this._currentPaymentMethodsIds$;
    }

    defaultPaymentMethodSettings: FormPaymentMethodSetting[] = [];
    balanceCommonSettings: BalanceCommonSettings | null = null;

    private readonly _currentPaymentMethodsIds$ = new BehaviorSubject<number[]>([]);

    constructor() {
        super();
    }

    initForm(): void {
        this.form.get(UserBaseFormControls.Role)?.setValue(UserRole.Trader, { emitEvent: false });
        this.form.addControl(UserTraderFormControls.TeamLeadId, new FormControl(''), {
            emitEvent: false
        });
        this.form.addControl(SETTINGS_FORM_GROUP_NAME, this.settingsFormGroup, { emitEvent: false });
        this.settingsFormGroup.addControl(
            UserTraderFormControls.MinTrustBalance,
            new FormControl('', [Validators.required]),
            { emitEvent: false }
        );
        this.settingsFormGroup.addControl(
            UserTraderFormControls.MinTradeBalance,
            new FormControl('', [Validators.required]),
            { emitEvent: false }
        );
        this.settingsFormGroup.addControl(
            PAYMENT_METHOD_SETTINGS_FORM_ARRAY_NAME,
            this.paymentMethodSettingsFormArray,
            {
                emitEvent: false
            }
        );
    }

    override applyUserDetails(userDetails?: UpsertUserData) {
        super.applyUserDetails(userDetails);
        this.form.get(UserTraderFormControls.TeamLeadId)?.setValue(userDetails?.teamLeadId, { emitEvent: false });
    }

    setSettingsData(
        paymentMethodSettingsData: PaymentMethodSetting[],
        balanceCommonSettings: BalanceCommonSettings | null
    ): void {
        this.defaultPaymentMethodSettings = paymentMethodSettingsData
            .filter((item) => item.role === this.role)
            .map((item) => {
                return {
                    [UserTraderFormControls.PaymentMethodId]: item.paymentMethodId,
                    [UserTraderFormControls.DailyLimit]: item[PaymentMethodSettingsKeys.DailyLimit],
                    [UserTraderFormControls.ThresholdOrderAmount]: item[PaymentMethodSettingsKeys.ThresholdOrderAmount],
                    [UserTraderFormControls.UpToThresholdCommissionRate]:
                        item[PaymentMethodSettingsKeys.UpToThresholdCommissionRate],
                    [UserTraderFormControls.EqualOrAboveThresholdCommissionRate]:
                        item[PaymentMethodSettingsKeys.EqualOrAboveThresholdCommissionRate]
                };
            });

        this.balanceCommonSettings = balanceCommonSettings;
    }

    applySettingsToFormInitially(count?: number): void {
        this.settingsFormGroup
            .get(UserTraderFormControls.MinTrustBalance)
            ?.setValue(this.balanceCommonSettings?.minTrustBalance, { emitEvent: false });
        this.settingsFormGroup
            .get(UserTraderFormControls.MinTradeBalance)
            ?.setValue(this.balanceCommonSettings?.minTradeBalance, { emitEvent: false });

        this.fillPaymentMethodSettings(count);
    }

    revertFormPaymentMethodSettingsToInitialMode(): void {
        this.paymentMethodSettingsFormArray.clear();
        this.fillPaymentMethodSettings();
    }

    setSettingsFormToCustomMode(): void {
        this.paymentMethodSettingsFormArray.clear();
        const setting = this.defaultPaymentMethodSettings?.[0];

        if (setting) {
            this.addPaymentMethodSettingsDataItem(setting);
            this._currentPaymentMethodsIds$.next([setting.paymentMethodId]);
        }
    }

    addPaymentMethodInCustomMode(): void {
        const currentIds = this._currentPaymentMethodsIds$.getValue();
        const defaultIds = this.defaultPaymentMethodSettings.map(
            (setting) => setting[UserTraderFormControls.PaymentMethodId]
        );

        if (currentIds.length === defaultIds.length) {
            return;
        }

        const setting = this.defaultPaymentMethodSettings?.find(
            (setting) => !currentIds.includes(setting[UserTraderFormControls.PaymentMethodId])
        );

        if (setting) {
            this.addPaymentMethodSettingsDataItem(setting);
            this._currentPaymentMethodsIds$.next([...currentIds, setting.paymentMethodId]);
        }
    }

    deletePaymentMethodInCustomMode(id: number): void {
        for (let i = 0; i < this.paymentMethodSettingsFormArray.controls.length; i++) {
            if (this.paymentMethodSettingsFormArray.value[i][UserTraderFormControls.PaymentMethodId] === id) {
                this.paymentMethodSettingsFormArray.removeAt(i, { emitEvent: false });
                break;
            }
        }

        this._currentPaymentMethodsIds$.next(
            this._currentPaymentMethodsIds$.getValue().filter((methodId) => methodId !== id)
        );
    }

    setCurrentMethodsIds(ids: number[]): void {
        this._currentPaymentMethodsIds$.next(ids);
    }

    private fillPaymentMethodSettings(count?: number): void {
        const paymentMethods = _.take(
            this.defaultPaymentMethodSettings,
            count ?? this.defaultPaymentMethodSettings.length
        );

        paymentMethods?.forEach((setting) => {
            this.addPaymentMethodSettingsDataItem(setting);
        });

        this._currentPaymentMethodsIds$.next(
            paymentMethods
                ?.map((paymentMethodSetting) => paymentMethodSetting[UserTraderFormControls.PaymentMethodId])
                ?.filter((id) => !!id) ?? []
        );
    }

    private addPaymentMethodSettingsDataItem(setting: FormPaymentMethodSetting): void {
        this.paymentMethodSettingsFormArray.push(
            new FormGroup({
                [UserTraderFormControls.PaymentMethodId]: new FormControl(setting.paymentMethodId, [
                    Validators.required
                ]),
                [UserTraderFormControls.DailyLimit]: new FormControl(
                    Number(setting[UserTraderFormControls.DailyLimit]),
                    [Validators.required]
                ),
                [UserTraderFormControls.ThresholdOrderAmount]: new FormControl(
                    Number(setting[UserTraderFormControls.ThresholdOrderAmount]),
                    [Validators.required]
                ),
                [UserTraderFormControls.UpToThresholdCommissionRate]: new FormControl(
                    Number(setting[UserTraderFormControls.UpToThresholdCommissionRate]),
                    [Validators.required]
                ),
                [UserTraderFormControls.EqualOrAboveThresholdCommissionRate]: new FormControl(
                    Number(setting[UserTraderFormControls.EqualOrAboveThresholdCommissionRate]),
                    [Validators.required]
                )
            }),
            { emitEvent: false }
        );
    }
}
